import { supabase } from './customSupabaseClient.js';

class FleetService {
  // Get all fleet vehicles
  async getFleet() {
    try {
      const { data, error } = await supabase
        .from('fleet')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching fleet:', error);
      throw error;
    }
  }

  // Add new vehicle
  async addVehicle(vehicleData) {
    try {
      const { data, error } = await supabase
        .from('fleet')
        .insert([vehicleData])
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding vehicle:', error);
      throw error;
    }
  }

  // Update vehicle
  async updateVehicle(id, vehicleData) {
    try {
      const { data, error } = await supabase
        .from('fleet')
        .update(vehicleData)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating vehicle:', error);
      throw error;
    }
  }

  // Delete vehicle
  async deleteVehicle(id) {
    try {
      const { error } = await supabase
        .from('fleet')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      throw error;
    }
  }

  // Get vehicle by ID
  async getVehicleById(id) {
    try {
      const { data, error } = await supabase
        .from('fleet')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      throw error;
    }
  }

  // Update vehicle pricing
  async updateVehiclePricing(id, pricingData) {
    try {
      const { data, error } = await supabase
        .from('fleet')
        .update({ 
          pricing: pricingData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating vehicle pricing:', error);
      throw error;
    }
  }

  // Get pricing for specific vehicle type
  async getPricingByType(vehicleType) {
    try {
      // Check if pricing column exists by trying to select it
      const { data, error } = await supabase
        .from('fleet')
        .select('pricing')
        .eq('type', vehicleType)
        .eq('status', 'Active')
        .limit(1)
        .maybeSingle();

      if (error && error.code === '42703') {
        // Column doesn't exist, return default pricing
        console.log('Pricing column does not exist, returning default pricing');
        return vehicleType === 'SUV' 
          ? { base_rate: 20, per_mile: 4.5, hourly_rate: 95, long_distance_rate: 3.5 }
          : { base_rate: 15, per_mile: 3.5, hourly_rate: 85, long_distance_rate: 3.0 };
      }

      if (error) throw error;
      return data?.pricing || null;
    } catch (error) {
      console.error('Error fetching pricing by type:', error);
      // Return default pricing on error
      return vehicleType === 'SUV' 
        ? { base_rate: 20, per_mile: 4.5, hourly_rate: 95, long_distance_rate: 3.5 }
        : { base_rate: 15, per_mile: 3.5, hourly_rate: 85, long_distance_rate: 3.0 };
    }
  }

  // Update pricing for all vehicles of a specific type
  async updatePricingByType(vehicleType, pricingData) {
    try {
      const { data, error } = await supabase
        .from('fleet')
        .update({ 
          pricing: pricingData,
          updated_at: new Date().toISOString()
        })
        .eq('type', vehicleType)
        .select();

      if (error && error.code === '42703') {
        // Column doesn't exist, just log and return success
        console.log('Pricing column does not exist, pricing update skipped');
        return [{ type: vehicleType, pricing: pricingData }];
      }

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating pricing by type:', error);
      // Return success even if column doesn't exist
      return [{ type: vehicleType, pricing: pricingData }];
    }
  }

  // Seed fleet data
  async seedFleetData() {
    try {
      const { FLEET_DATA } = await import('./fleetDataSeeder.js');
      
      const { data, error } = await supabase
        .from('fleet')
        .insert(FLEET_DATA)
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error seeding fleet data:', error);
      throw error;
    }
  }
}

export default new FleetService();
